import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const POPULAR = [
  'Cancer', 'Diabetes', 'Heart Disease', 'COVID-19', 'Alzheimer Disease',
  'Depression', 'Breast Cancer', 'Hypertension', 'Obesity', 'Stroke',
  'Parkinson Disease', 'Lung Cancer', 'Asthma', 'HIV Infection', 'Arthritis',
  'Type 2 Diabetes', 'Anxiety Disorders', 'Chronic Pain', 'Multiple Sclerosis', 'Kidney Disease'
];

function Highlight({ text, query }) {
  if (!query || !text) return <span>{text}</span>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <span>{text}</span>;
  return (
    <span>
      {text.slice(0, idx)}
      <strong style={{ color: 'var(--paper)', fontWeight: 700 }}>{text.slice(idx, idx + query.length)}</strong>
      {text.slice(idx + query.length)}
    </span>
  );
}

export default function SearchAutocomplete({ onSearch, onGuestClick, isGuest, initialValue = '' }) {
  const navigate = useNavigate();
  const [value, setValue] = useState(initialValue);
  const [focused, setFocused] = useState(false);
  const [results, setResults] = useState({ conditions: [], studies: [] });
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  const fetchSuggestions = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults({ conditions: [], studies: [] }); setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await axios.get('/api/trials/autocomplete', { params: { q } });
      setResults(data);
    } catch {
      setResults({ conditions: [], studies: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (value.length >= 2) {
      debounceRef.current = setTimeout(() => fetchSuggestions(value), 300);
    } else {
      setResults({ conditions: [], studies: [] });
      setLoading(false);
    }
    return () => clearTimeout(debounceRef.current);
  }, [value, fetchSuggestions]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setFocused(false); setActiveIdx(-1);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const showDropdown = focused;
  const showPopular = focused && value.length < 2;
  const hasResults = results.conditions.length > 0 || results.studies.length > 0;

  // All navigable items flattened
  const allItems = showPopular
    ? POPULAR.slice(0, 10).map(t => ({ type: 'popular', value: t }))
    : [
        ...results.conditions.map(c => ({ type: 'condition', value: c })),
        ...results.studies.map(s => ({ type: 'study', value: s.title, nctId: s.nctId, status: s.status }))
      ];

  const handleSelect = (item) => {
    if (item.type === 'study') {
      setFocused(false);
      navigate(`/study/${item.nctId}`);
    } else {
      setValue(item.value);
      setFocused(false);
      setActiveIdx(-1);
      onSearch(item.value);
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (isGuest) { onGuestClick(); return; }
    setFocused(false);
    onSearch(value);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || allItems.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, allItems.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, -1)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIdx >= 0 && allItems[activeIdx]) handleSelect(allItems[activeIdx]);
      else handleSubmit();
    }
    else if (e.key === 'Escape') { setFocused(false); setActiveIdx(-1); }
  };

  const STATUS_DOT = {
    RECRUITING: '#16a34a', NOT_YET_RECRUITING: '#2563eb',
    COMPLETED: '#9ca3af', TERMINATED: '#dc2626', default: '#9ca3af'
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
      <form onSubmit={handleSubmit} style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          style={{
            width: '100%', padding: '17px 120px 17px 22px',
            fontSize: 16, border: `1px solid ${focused ? 'var(--muted)' : 'var(--ink-2)'}`,
            borderRadius: 0, background: 'var(--ink)', color: 'var(--paper)',
            outline: 'none', fontFamily: 'var(--font-body)',
            transition: 'border-color 0.2s', boxSizing: 'border-box'
          }}
          placeholder="Search by condition, disease, NCT ID, or keyword..."
          value={value}
          onChange={e => { setValue(e.target.value); setActiveIdx(-1); }}
          onFocus={() => { if (isGuest) { onGuestClick(); return; } setFocused(true); }}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          spellCheck="false"
        />
        <button
          type="submit"
          style={{
            position: 'absolute', right: 5, top: '50%', transform: 'translateY(-50%)',
            background: 'var(--paper)', color: 'var(--ink)', border: 'none',
            padding: '9px 22px', borderRadius: 0, fontWeight: 700,
            fontSize: 14, cursor: 'pointer', zIndex: 2, fontFamily: 'var(--font-body)'
          }}
        >
          Search
        </button>
      </form>

      {showDropdown && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
          background: 'var(--ink)', border: '1px solid var(--ink-2)', borderRadius: 0,
          zIndex: 500, boxShadow: '8px 8px 0 rgba(10,10,10,0.12)',
          maxHeight: 420, overflowY: 'auto'
        }}>

          {/* Loading */}
          {loading && (
            <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 10, color: 'var(--muted)', fontSize: 13 }}>
              <div className="spin" style={{ width: 14, height: 14, border: '1.5px solid var(--ink-2)', borderTopColor: 'var(--muted-2)', borderRadius: '50%' }} />
              Searching conditions...
            </div>
          )}

          {/* Popular */}
          {showPopular && !loading && (
            <>
              <div style={{ padding: '10px 18px 6px', fontSize: 10, color: 'var(--muted-2)', letterSpacing: '2px', textTransform: 'uppercase', fontFamily: 'var(--font-mono)' }}>
                Popular Searches
              </div>
              {POPULAR.slice(0, 10).map((term, i) => (
                <div
                  key={term}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '9px 18px', cursor: 'pointer', fontSize: 14,
                    color: activeIdx === i ? 'var(--paper)' : 'var(--muted)',
                    background: activeIdx === i ? 'var(--ink-2)' : 'transparent',
                    transition: 'all 0.1s'
                  }}
                  onMouseEnter={() => setActiveIdx(i)}
                  onMouseDown={(e) => { e.preventDefault(); handleSelect({ type: 'popular', value: term }); }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--muted-2)', flexShrink: 0 }}>
                    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                  </svg>
                  {term}
                </div>
              ))}
            </>
          )}

          {/* Conditions */}
          {!loading && results.conditions.length > 0 && (
            <>
              <div style={{ padding: '10px 18px 6px', fontSize: 10, color: 'var(--muted-2)', letterSpacing: '2px', textTransform: 'uppercase', fontFamily: 'var(--font-mono)', borderTop: showPopular ? '1px solid var(--ink-2)' : 'none' }}>
                Conditions & Diseases
              </div>
              {results.conditions.map((cond, i) => {
                const idx = i;
                return (
                  <div
                    key={cond}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '9px 18px', cursor: 'pointer', fontSize: 14,
                      color: activeIdx === idx ? 'var(--paper)' : 'var(--muted)',
                      background: activeIdx === idx ? 'var(--ink-2)' : 'transparent',
                      transition: 'all 0.1s'
                    }}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onMouseDown={(e) => { e.preventDefault(); handleSelect({ type: 'condition', value: cond }); }}
                  >
                    <span style={{ fontSize: 14, color: 'var(--muted-2)', flexShrink: 0 }}>◎</span>
                    <Highlight text={cond} query={value} />
                  </div>
                );
              })}
            </>
          )}

          {/* Studies */}
          {!loading && results.studies.length > 0 && (
            <>
              <div style={{ padding: '10px 18px 6px', fontSize: 10, color: 'var(--muted-2)', letterSpacing: '2px', textTransform: 'uppercase', fontFamily: 'var(--font-mono)', borderTop: '1px solid var(--ink-2)' }}>
                Studies
              </div>
              {results.studies.map((study, i) => {
                const idx = results.conditions.length + i;
                const dotColor = STATUS_DOT[study.status] || STATUS_DOT.default;
                return (
                  <div
                    key={study.nctId}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 12,
                      padding: '9px 18px', cursor: 'pointer',
                      background: activeIdx === idx ? 'var(--ink-2)' : 'transparent',
                      transition: 'all 0.1s'
                    }}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onMouseDown={(e) => { e.preventDefault(); handleSelect({ type: 'study', value: study.title, nctId: study.nctId }); }}
                  >
                    <span style={{ marginTop: 3, color: 'var(--muted-2)', flexShrink: 0, fontSize: 12 }}>▸</span>
                    <div>
                      <div style={{ fontSize: 13, color: activeIdx === idx ? 'var(--paper)' : 'var(--muted)', lineHeight: 1.4 }}>
                        <Highlight text={study.title} query={value} />
                      </div>
                      <div style={{ fontSize: 11, marginTop: 2, display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-mono)' }}>
                        <span style={{ color: 'var(--muted-2)' }}>{study.nctId}</span>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
                        <span style={{ color: 'var(--muted-2)' }}>{study.status?.replace(/_/g, ' ')}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {/* No results */}
          {!loading && value.length >= 2 && !hasResults && (
            <div style={{ padding: '16px 18px', color: 'var(--muted-2)', fontSize: 13, textAlign: 'center' }}>
              No suggestions — press Enter to search anyway
            </div>
          )}

          <div style={{ padding: '8px 18px', fontSize: 10, color: 'var(--muted)', fontFamily: 'var(--font-mono)', borderTop: '1px solid var(--ink-2)', display: 'flex', gap: 16 }}>
            <span>↑↓ navigate</span><span>↵ select</span><span>Esc close</span>
          </div>
        </div>
      )}
    </div>
  );
}